import { createForm, useStore } from "@tanstack/solid-form";
import {
  IoCloseOutline,
  IoCopyOutline,
  IoLinkOutline,
  IoMailOutline,
} from "solid-icons/io";
import { createMemo, Index, Show } from "solid-js";
import { toast } from "solid-sonner";
import { z } from "zod";
import { useI18n } from "@/api/i18n";
import { Button } from "@/components/ui/button";
import {
  FieldContainer,
  FieldContainerLabel,
  FieldInfo,
  Input,
} from "@/components/ui/input";
import { usePostHog } from "@/hooks/usePostHog";
import { useCreateEmailInvitation, useGetInvitationLink } from "../api";

const emailsSchema = z.object({
  emails: z
    .array(z.string().email("Email non valida"))
    .min(1, "Inserisci almeno un'email valida"),
});

type FormValues = z.infer<typeof emailsSchema>;

interface InviteFreelancerFormProps {
  onSuccess?: () => void;
  onCancel?: () => void;
  /** Se true, nasconde il pulsante Annulla (usato nella tab) */
  hideCancel?: boolean;
}

export const InviteFreelancerForm = (props: InviteFreelancerFormProps) => {
  const { t } = useI18n();
  const { capture } = usePostHog();

  const createEmailInvitation = useCreateEmailInvitation();
  const invitationLink = useGetInvitationLink();

  const form = createForm(() => ({
    defaultValues: {
      emails: [""],
    } as FormValues,
    schema: emailsSchema,
    onSubmit: async ({ value }) => {
      capture("send_freelancer_invitation");
      const validEmails = value.emails
        .map((email) => email.trim())
        .filter((email) => {
          if (!email) return false;
          try {
            z.email().parse(email);
            return true;
          } catch {
            return false;
          }
        });

      if (validEmails.length === 0) {
        toast.error(t("freelancerInvitations.modal.toast.noValidEmails"));
        return;
      }

      const results = await Promise.allSettled(
        validEmails.map((email) =>
          createEmailInvitation.mutateAsync({ email }),
        ),
      );

      let successCount = 0;
      const errors: Array<{ email: string; error: string }> = [];

      results.forEach((result, index) => {
        const email = validEmails[index];
        if (result.status === "fulfilled") {
          successCount++;
        } else {
          const error = result.reason;
          const errorMessage =
            (
              error as {
                response?: { data?: { msg?: string } };
                message?: string;
              }
            )?.response?.data?.msg ||
            (error as { message?: string })?.message ||
            "Unknown error";
          errors.push({ email, error: errorMessage });

          if (errorMessage === "Recommendation already exists") {
            toast.error(
              t(
                "freelancerInvitations.modal.toast.recommendationAlreadyExists",
              ),
            );
          } else if (errorMessage === "Cannot invite yourself") {
            toast.error(
              t("freelancerInvitations.modal.toast.cannotInviteYourself"),
            );
          } else if (
            errorMessage === "Invitation already pending for this email"
          ) {
            toast.error(
              t("freelancerInvitations.modal.toast.invitationAlreadyPending"),
            );
          } else {
            toast.error(
              errorMessage || t("freelancerInvitations.modal.toast.error"),
            );
          }
        }
      });

      if (successCount > 0) {
        const message =
          successCount === 1
            ? t("freelancerInvitations.modal.toast.inviteSentSingular")
            : t("freelancerInvitations.modal.toast.invitesSentPlural", {
                count: successCount,
              });
        toast.success(message);
        form.setFieldValue("emails", [""]);
        if (errors.length === 0) {
          props.onSuccess?.();
        }
      }
    },
  }));

  const addEmailField = () => {
    const currentEmails = form.state.values.emails || [""];
    form.setFieldValue("emails", [...currentEmails, ""]);
  };

  const removeEmailField = (index: number) => {
    const currentEmails = form.state.values.emails || [""];
    const newEmails = currentEmails.filter((_, i) => i !== index);
    form.setFieldValue("emails", newEmails.length > 0 ? newEmails : [""]);
  };

  const emails = useStore(form.store, (store) => store.values.emails);

  const hasValidEmail = createMemo(() => {
    return emails().some((email) => {
      const trimmed = email?.trim() ?? "";
      if (!trimmed) return false;
      try {
        z.email().parse(trimmed);
        return true;
      } catch {
        return false;
      }
    });
  });

  const copyLink = async () => {
    const link = invitationLink.data?.link;
    if (link) {
      capture("click_copy_freelancer_invitation_link");
      await navigator.clipboard.writeText(link);
      toast.success(t("freelancerInvitations.modal.toast.linkCopied"));
    }
  };

  return (
    <div class="space-y-4">
      {/* Sezione Link */}
      <div class="grid grid-cols-[1.75rem_1fr] gap-x-2 items-start">
        <div class="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
          <IoLinkOutline class="w-4 h-4 text-primary" />
        </div>
        <div class="space-y-2 min-w-0">
          <h3 class="font-semibold text-gray-900 text-sm">
            {t("freelancerInvitations.modal.link.title")}
          </h3>
          <p class="text-sm text-gray-600">
            {t("freelancerInvitations.modal.link.description")}
          </p>
          <div class="space-y-1">
            <p class="text-xs font-medium text-gray-700">
              {t("freelancerInvitations.modal.link.label")}
            </p>
            <Show
              when={invitationLink.isError}
              fallback={
                <Show
                  when={invitationLink.isSuccess && invitationLink.data}
                  fallback={
                    <div class="p-3 bg-gray-50 rounded-lg animate-pulse">
                      <p class="text-sm text-gray-500">
                        {t("freelancerInvitations.modal.link.loading")}
                      </p>
                    </div>
                  }
                >
                  <div class="flex gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <Input
                      value={invitationLink.data?.link || ""}
                      readOnly
                      class="flex-1 font-mono text-xs bg-white border-0 focus-visible:ring-0 min-w-0"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={copyLink}
                      title={t("freelancerInvitations.modal.link.copyButton")}
                      class="shrink-0 hover:bg-primary/10"
                    >
                      <IoCopyOutline class="w-5 h-5 text-gray-600" />
                    </Button>
                  </div>
                </Show>
              }
            >
              <div class="p-3 bg-red-50 rounded-lg border border-red-200">
                <p class="text-sm text-red-600">
                  Errore nel caricamento del link. Riprova più tardi.
                </p>
              </div>
            </Show>
          </div>
        </div>
      </div>

      {/* Divider */}
      <div class="relative py-2">
        <div class="absolute inset-0 flex items-center">
          <div class="w-full border-t border-gray-200" />
        </div>
        <div class="relative flex justify-center text-xs">
          <span class="px-3 bg-card text-gray-500">
            {t("freelancerInvitations.modal.separator")}
          </span>
        </div>
      </div>

      {/* Sezione Email */}
      <div class="grid grid-cols-[1.75rem_1fr] gap-x-2 items-start">
        <div class="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
          <IoMailOutline class="w-4 h-4 text-primary" />
        </div>
        <div class="space-y-2 min-w-0">
          <h3 class="font-semibold text-gray-900 text-sm">
            {t("freelancerInvitations.modal.email.title")}
          </h3>
          <p class="text-sm text-gray-600">
            {t("freelancerInvitations.modal.email.description")}
          </p>
          <div class="space-y-2">
            <Index each={emails()}>
              {(_, index) => (
                <form.Field
                  name={`emails[${index}]`}
                  validators={{
                    onChange: ({ value }) => {
                      if (!value || value.trim() === "") return undefined;
                      const result = z.email().safeParse(value.trim());
                      return result.success
                        ? undefined
                        : result.error.issues[0]?.message || "Email non valida";
                    },
                  }}
                >
                  {(field) => (
                    <div class="flex gap-2">
                      <FieldContainer class="flex-1 min-w-0">
                        <Show when={index === 0}>
                          <FieldContainerLabel for={field().name}>
                            email*
                          </FieldContainerLabel>
                        </Show>
                        <Input
                          id={field().name}
                          name={field().name}
                          type="email"
                          placeholder={t(
                            "freelancerInvitations.modal.email.placeholder",
                          )}
                          value={field().state.value || ""}
                          onInput={(e) => field().handleChange(e.target.value)}
                          onChange={(e) => field().handleChange(e.target.value)}
                          onBlur={() => field().handleBlur()}
                        />
                        <FieldInfo field={field()} />
                      </FieldContainer>
                      <Show when={emails().length > 1}>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeEmailField(index)}
                          title={t("freelancerInvitations.modal.email.remove")}
                          class="shrink-0 text-gray-400 hover:text-gray-600 hover:bg-gray-100 self-end"
                        >
                          <IoCloseOutline class="w-5 h-5" />
                        </Button>
                      </Show>
                    </div>
                  )}
                </form.Field>
              )}
            </Index>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={addEmailField}
              class="w-full justify-start text-primary hover:text-primary/80 hover:bg-primary/10 px-3"
            >
              {t("freelancerInvitations.modal.email.addAnother")}
            </Button>
          </div>
        </div>
      </div>

      {/* Footer con pulsanti */}
      <div class="flex gap-2 pt-2">
        <Show when={!props.hideCancel && props.onCancel}>
          <Button variant="outline" onClick={props.onCancel}>
            {t("freelancerInvitations.modal.actions.cancel")}
          </Button>
        </Show>
        <Button
          onClick={() => form.handleSubmit()}
          disabled={createEmailInvitation.isPending || !hasValidEmail()}
        >
          <Show
            when={!createEmailInvitation.isPending}
            fallback={t("freelancerInvitations.modal.actions.sending")}
          >
            {t("freelancerInvitations.modal.actions.send")}
          </Show>
        </Button>
      </div>
    </div>
  );
};
